import { Modifier } from '@amberscript/amberscript-draftjs/lib/Draft';
import { List } from 'immutable';
import TranscriptWord from '../models/TranscriptWord';
import { getWordsBefore, getWordsAfter } from './wordsUtils'
import uuidV4 from 'uuid/v4';

const numberOfSpaces = (text) => {
    const spaces = text.match(/\s/gi)
    return spaces != null ? spaces.length : 0;
}

// Utility functions for space checking
const hasLeadingSpace = (text, offset) => /\s/.test(text[offset - 1]) || offset === 0;
const hasTrailingSpace = (text, offset, length) => /\s/.test(text[offset]) || offset === length;
const isSingleSpace = (text) => /\s/.test(text) && text.length === 1;

// Utility functions for word manipulation
const updateWordText = (word, newText) => word.update('text', val => val + newText).set('pristine', false);
const createNewWord = (text, start, end) => TranscriptWord({
    id: uuidV4(),
    text,
    start,
    end,
    conf: null,
    pristine: false
});

// Combined recursive function for getting times from adjacent blocks
const getTimeFromAdjacentBlocks = (content, blockKey, direction, defaultValue) => {
    const getAdjacentBlock = direction === 'prev' ? content.getBlockBefore : content.getBlockAfter;
    const block = getAdjacentBlock(blockKey);
    
    if (block === undefined) return defaultValue;

    const words = block.getIn(['data', 'words']);
    if (words && words.size > 0) {
        return direction === 'prev' ? words.last().get('end') : words.first().get('start');
    }
    return getTimeFromAdjacentBlocks(content, block.getKey(), direction, defaultValue);
};

// Split word filtering and correction
const filterEmptyWords = (words) => words.filter(record => record.get('text').trim().length !== 0);
const correctWordSpaces = (words) => words.map(record => 
    record.update('text', val => val.replace(/\s\s+/g, ' ').trim())
);

const filterAndCorrectedWords = (words) => {
    const filteredWords = filterEmptyWords(words);
    return correctWordSpaces(filteredWords);
};

/**
 * replaceText takes a ContentState object, a string and a selection
 * object and returns a new ContentState.
 *
 * It synchronises the text displayed with the Words model (located
 * in block.data.words)
 *
 * The Words model contains start and end times, and is used to
 * decorate the blocks with words (only if the current time of
 * the audio playback is situated within a block)
 *
 * replaceText is used for all editor operations and all
 * editor operations can be modelled with or in combination with
 * this method.
 * @param contentState: ContentState
 * @param text: string
 * @param selection: Selection
 * @returns ContentState
 */
const replaceText = (content, text, selection) => {
    const startKey = selection.getStartKey();
    const endKey = selection.getEndKey();
    const startOffset = selection.getStartOffset();
    const endOffset = selection.getEndOffset();

    const startBlock = content.getBlockForKey(startKey);
    const endBlock = content.getBlockForKey(endKey);

    const startText = startBlock.getText();
    const endText = endBlock.getText();

    const startBlockWords = startBlock.getIn(['data', 'words'])
    const endBlockWords = endBlock.getIn(['data', 'words'])

    const startOffsetWithoutSpaces = startOffset - numberOfSpaces(startText.slice(0, startOffset))
    const endOffsetWithoutSpaces = endOffset - numberOfSpaces(endText.slice(0, endOffset))

    let words = List(), start = 0, end = 0;

    const wordsBefore = getWordsBefore(startBlockWords, startOffsetWithoutSpaces)
    const wordsAfter = getWordsAfter(endBlockWords, endOffsetWithoutSpaces)

    const leadingSpace = hasLeadingSpace(startText, startOffset);
    const trailingSpace = hasTrailingSpace(endText, endOffset, endText.length);
    const isSpace = isSingleSpace(text);

    if (wordsBefore.size > 0) {
        start = wordsBefore.last().get('end')
    } else {
        start = getTimeFromAdjacentBlocks(content, startKey, 'prev', 0)
    }

    if (wordsAfter.size > 0) {
        end = wordsAfter.first().get('start')
    } else {
        end = getTimeFromAdjacentBlocks(content, endKey, 'next', start)
    }

    if (isSpace && selection.isCollapsed() && (leadingSpace || trailingSpace)) {
        return content;
    } else if (isSpace && !leadingSpace && !trailingSpace) {
        words = wordsBefore.slice(0, -1)
            .push(wordsBefore.last().update('end', val => wordsAfter.first().get('start')).set('pristine', false))
            .push(wordsAfter.first().set('pristine', false).set('id', uuidV4()))
            .concat(wordsAfter.slice(1))
    } else if (leadingSpace && trailingSpace) {
        words = wordsBefore
            .push(createNewWord(text, start, end))
            .concat(wordsAfter)
    } else if (wordsBefore.size > 0 && trailingSpace) {
        words = wordsBefore.slice(0, -1)
            .push(updateWordText(wordsBefore.last(), text))
            .concat(wordsAfter)
    } else if (wordsAfter.size > 0 && leadingSpace) {
        words = wordsBefore
            .push(updateWordText(wordsAfter.first(), text))
            .concat(wordsAfter.slice(1))
    } else if (!trailingSpace && !leadingSpace) {
        words = wordsBefore.slice(0, -1)
            .push(wordsBefore.last().update(word => {
                const wordAfter = wordsAfter.first();
                if (wordAfter) {
                    return word.merge({
                        text: word.get('text') + text + wordAfter.get('text'),
                        start: word.get('start'),
                        end: wordAfter.get('end'),
                        pristine: false
                    })
                } else {
                    return word.merge({
                        text: word.get('text') + text,
                        pristine: false
                    })
                }
            }))
            .concat(wordsAfter.slice(1))
    }

    if (selection.isCollapsed()) {
        content = Modifier.insertText(
            content,
            selection,
            text,
            startBlock.getInlineStyleAt(startOffset)
        )
    } else {
        content = Modifier.replaceText(
            content,
            selection,
            text,
        )
    }

    content = Modifier.mergeBlockData(
        content,
        content.getSelectionAfter(),
        {
            words: filterAndCorrectedWords(words),
        }
    )

    return content
}

export default replaceText;